import { PsvReviewIssueStatus } from "@api/shared-runtime/enums";
import { PsvReviewStatus } from "@api/shared-runtime/psv-review";
import { formatCredentialTimestamp } from "../../../lib/format-date";
import type { ReportIssue } from "../types";

export const NO_VALUE = "—";

export const ISSUE_STATUS_LABELS: Record<PsvReviewIssueStatus, string> = {
  [PsvReviewIssueStatus.New]: "New",
  [PsvReviewIssueStatus.Changed]: "Changed",
  [PsvReviewIssueStatus.Unchanged]: "Unchanged",
  [PsvReviewIssueStatus.Expired]: "Expired",
};

const CHANGE_FIELD_LABELS: Record<string, string> = {
  exclusionType: "Exclusion type",
  exclusionDate: "Exclusion date",
  reinstatementDate: "Reinstatement date",
  waiverDate: "Waiver date",
  waiverState: "Waiver state",
  program: "Program",
  activeDate: "Effective date",
  terminationDate: "End date",
  recordStatus: "Record status",
  programs: "Programs",
  identifiers: "Identifiers",
  providerType: "Provider type",
  reinstatedAt: "Reinstated",
  effectiveDate: "Effective date",
  endDate: "End date",
  "identityComparisons.npi": "NPI match",
  "identityComparisons.dateOfBirth": "Date of birth match",
  "identityComparisons.licenseNumber": "License number match",
};

export function changeFieldLabel(field: string): string {
  return CHANGE_FIELD_LABELS[field] ?? field;
}

const EARLIER_DECISION_PHRASES: Partial<Record<PsvReviewStatus, string>> = {
  [PsvReviewStatus.Accepted]: "accepted",
  [PsvReviewStatus.Concern]: "marked concern",
};

/** When the earlier decision was made, in the viewer's time zone like the
 * report's other timestamps. A calendar-date formatter would show a decision
 * made in the evening as the next day's UTC date. */
function earlierDecisionDate(
  previous: NonNullable<ReportIssue["previous"]>,
): string {
  return formatCredentialTimestamp(previous.decidedAt ?? previous.runCreatedAt);
}

/** "accepted Jan 3, 2026, 4:15 PM by Jane Doe" */
export function earlierDecisionLine(
  previous: NonNullable<ReportIssue["previous"]>,
): string {
  const phrase = EARLIER_DECISION_PHRASES[previous.status];
  if (!phrase) {
    return "not decided";
  }
  const line = `${phrase} ${earlierDecisionDate(previous)}`;
  const name = previous.decidedBy?.name;
  return name ? `${line} by ${name}` : line;
}

function countLabel({
  count,
  singular,
  plural,
}: {
  count: number;
  singular: string;
  plural: string;
}): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** The item-level summary, e.g. "1 new match · 2 unchanged since an earlier
 * decision · 1 changed". Null when there are no issues. */
export function issueSummary(issues: readonly ReportIssue[]): string | null {
  const count = (status: PsvReviewIssueStatus) =>
    issues.filter((issue) => issue.status === status).length;
  const parts = [
    {
      count: count(PsvReviewIssueStatus.New),
      singular: "new match",
      plural: "new matches",
    },
    {
      count: count(PsvReviewIssueStatus.Unchanged),
      singular: "unchanged since an earlier decision",
      plural: "unchanged since an earlier decision",
    },
    {
      count: count(PsvReviewIssueStatus.Changed),
      singular: "changed",
      plural: "changed",
    },
    {
      count: count(PsvReviewIssueStatus.Expired),
      singular: "needs review (earlier decision expired)",
      plural: "need review (earlier decision expired)",
    },
  ]
    .filter((part) => part.count > 0)
    .map(countLabel);
  return parts.length ? parts.join(" · ") : null;
}

/** The earlier Concern decision among carried-over issues, if any. */
export function earlierConcernNote(
  issues: readonly ReportIssue[],
): string | null {
  const concern = issues.find(
    (issue) =>
      issue.status !== PsvReviewIssueStatus.New &&
      issue.previous?.status === PsvReviewStatus.Concern,
  );
  if (!concern?.previous) {
    return null;
  }
  return `Includes a match marked as a concern on ${earlierDecisionDate(concern.previous)}.`;
}
